import { CONTEXT_MENU_PT } from '@/shared/ui/RowContextMenu/contextMenuConfig';
import menuClasses from '@/shared/ui/RowContextMenu/RowContextMenu.module.scss';
import { getContextMenuEvent } from '@/shared/ui/RowContextMenu/contextMenuEvent';
import { useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { ContextMenu } from 'primereact/contextmenu';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { chatMessageEditService } from '@/entities/AppChat/api/chatMessageEditService';
import { createChatMessageEditSchema } from '@/entities/ChatDialog/model/editMessageSchema';
import { getErrorMessage } from '@/shared/lib/errors';
import { Modal } from '@/shared/ui/Modal/Modal';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { RequestError } from '@/shared/ui/RequestState';
import type { ChatDialogMessageResponse } from '@/core/generated/api';
import type { ChatMessageEditValues, ChatMessageContextMenuProps, ChatMessageEditDialogProps } from './types';
import classes from './ChatMessageEditDialog.module.scss';

const { TextareaField } = createRHFFields<ChatMessageEditValues>();

/** Показывает окно правки с исходной версией и сохранением ввода при ошибке */
const ChatMessageEditDialog = (
    { message, dialogId, onSaved, onClose }: ChatMessageEditDialogProps
) => {
    const form = useForm<ChatMessageEditValues>({
        resolver: zodResolver(createChatMessageEditSchema(Boolean(message.attachments?.length))),
        defaultValues: { text: message.text ?? '' },
        mode: 'onSubmit',
    });

    const attempt = useRef<{ text: string; requestId: string } | null>(null);
    const busy = form.formState.isSubmitting;

    /** Сохраняет правку только изменённого текста и ожидаемой версии */
    const save = async (values: ChatMessageEditValues): Promise<void> => {
        if (!form.formState.isDirty || !message.id) return;
        form.clearErrors('root.server');

        if (!attempt.current || attempt.current.text !== values.text) {
            attempt.current = {
                text: values.text,
                requestId: crypto.randomUUID()
            };
        }

        try {
            await chatMessageEditService.edit({
                dialogId,
                messageId: message.id,
                editChatMessageRequest: {
                    requestId: attempt.current.requestId,
                    expectedVersion: message.editVersion ?? 0,
                    text: values.text,
                },
            });

            await onSaved();
            onClose();
        } catch (error: unknown) {
            form.setError('root.server', {
                message: getErrorMessage(error, 'Не удалось сохранить сообщение'),
            });
        }
    };

    return <Modal
        visible
        title="Редактировать сообщение"
        onHide={onClose}
        closable={!busy}
        size="sm"
    >
        <FormProvider {...form}>
            <form
                aria-label="Редактирование сообщения"
                onSubmit={form.handleSubmit(save)}
            >
                <TextareaField
                    name="text"
                    label="Сообщение"
                    rows={3}
                    disabled={busy}
                    autoResize
                />
                {form.formState.errors.root?.server?.message &&
                    <RequestError
                        compact
                        message={form.formState.errors.root.server.message}
                    />
                }
                <div className={classes.actions}>
                    <ActionButton
                        type="button"
                        label="Отмена"
                        disabled={busy}
                        onClick={onClose}
                    />
                    <ActionButton
                        type="submit"
                        label="Сохранить"
                        loading={busy}
                        disabled={busy || !form.formState.isDirty}
                    />
                </div>
            </form>
        </FormProvider>
    </Modal>;
};

/** Показывает действия с исходящим ответом */
export const ChatMessageContextMenu = (props: ChatMessageContextMenuProps) => {
    const menu = useRef<ContextMenu>(null);
    const selected = useRef<ChatDialogMessageResponse>(props.message);
    const [target, setTarget] = useState<ChatDialogMessageResponse | null>(null);

    const eligible = props.enabled !== false && props.message.isOutgoing
        && props.message.kind?.id !== 'system';

    /** Показывает меню выбранного сообщения */
    const showMenu = (event: MouseEvent<HTMLElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        selected.current = props.message;
        menu.current?.show(getContextMenuEvent(event));
    };

    return <>
        {props.children(eligible
            ? showMenu
            : undefined)}
        {eligible &&
            <ContextMenu
                pt={CONTEXT_MENU_PT}
                className={menuClasses.contextMenu}
                ref={menu}
                ariaLabel="Действия с сообщением"
                model={[{
                    label: 'Редактировать',
                    icon: 'pi pi-pencil',
                    command: () => setTarget(selected.current),
                }]}
            />
        }
        {target &&
            <ChatMessageEditDialog
                {...props}
                message={target}
                onClose={() => setTarget(null)}
            />
        }
    </>;
};
